import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import CustomSnackbar from '../CustomSnackbar';

// -----------------------------------------------------------------------------
// Shared UI
// -----------------------------------------------------------------------------

const sectionSx = {
  p: 2,
  borderRadius: 1.5,
  backgroundColor: 'grey.100',
  border: '1px solid',
  borderColor: 'grey.300',
};

const sectionTitleSx = { mb: 1, fontWeight: 700, color: 'text.primary' };

// -----------------------------------------------------------------------------
// Data columns
// -----------------------------------------------------------------------------

// Shows the available data columns and updates the selected columns.
const DataColumnsSection = ({ availableColumns, columnLabels, selectedColumns, onChange }) => (
  <Box sx={sectionSx}>
    <FormControl component="fieldset" fullWidth>
      <FormLabel component="legend" sx={sectionTitleSx}>
        Data columns
      </FormLabel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          columnGap: 2,
          rowGap: 0.5,
        }}>
        {availableColumns.map(column => (
          <FormControlLabel
            key={column}
            control={
              <Checkbox
                checked={selectedColumns.includes(column)}
                onChange={() =>
                  onChange(
                    selectedColumns.includes(column)
                      ? selectedColumns.filter(selected => selected !== column)
                      : [...selectedColumns, column],
                  )
                }
              />
            }
            label={columnLabels[column] || column}
            sx={{ m: 0, minWidth: 0, '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' } }}
          />
        ))}
      </Box>
    </FormControl>
  </Box>
);

// -----------------------------------------------------------------------------
// Data intervals
// -----------------------------------------------------------------------------

const INTERVALS = [
  { label: 'Original records (5 minutes)', value: 5 },
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '3 hours', value: 180 },
  { label: '1 day', value: 1440 },
];

// Groups readings by the selected interval. For each group, it either keeps the
// first reading or calculates the average of every selected column.
const applyInterval = (rows, columns, intervalMinutes, calculation) => {
  // Five minutes is the original data, so nothing needs to be changed.
  if (intervalMinutes === 5) return rows;

  const intervalSeconds = intervalMinutes * 60;
  const groups = new Map();

  // Put every reading into its matching time group.
  rows.forEach(row => {
    const intervalStart = Math.floor(row.unix_time / intervalSeconds) * intervalSeconds;
    if (!groups.has(intervalStart)) groups.set(intervalStart, []);
    groups.get(intervalStart).push(row);
  });

  return [...groups.entries()].map(([unix_time, group]) => {
    // "Record" keeps the first real reading instead of making an average.
    if (calculation === 'record') return { ...group[0], unix_time };

    return columns.reduce(
      (row, column) => {
        // Ignore missing or invalid values when calculating the average.
        const values = group.map(record => Number(record[column])).filter(Number.isFinite);
        row[column] = values.length ? values.reduce((total, value) => total + value, 0) / values.length : '';
        return row;
      },
      { unix_time },
    );
  });
};

// Lets the user choose the data interval and how grouped readings are calculated.
const DataIntervalSection = ({ interval, calculation, onChange }) => (
  <Box sx={sectionSx}>
    <FormControl fullWidth>
      <FormLabel sx={sectionTitleSx}>Data intervals</FormLabel>
      <Select value={interval} onChange={event => onChange({ interval: Number(event.target.value) })}>
        {INTERVALS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {interval !== 5 && (
      <FormControl sx={{ mt: 2 }}>
        <FormLabel sx={sectionTitleSx}>How should each interval be calculated?</FormLabel>
        <RadioGroup value={calculation} onChange={event => onChange({ calculation: event.target.value })}>
          <FormControlLabel value="average" control={<Radio />} label="Average all records in each interval" />
          <FormControlLabel
            value="record"
            control={<Radio />}
            label="Keep one interval record and ignore records between intervals"
          />
        </RadioGroup>
      </FormControl>
    )}
  </Box>
);

// -----------------------------------------------------------------------------
// Data time range
// -----------------------------------------------------------------------------

const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  label: `${String(hour).padStart(2, '0')}:00`,
  value: hour,
}));
const MAX_CUSTOM_RANGE_DAYS = 31;

// Combines a selected date and hour into one date and time value.
const toDateTime = (date, hour) =>
  date?.isValid ? date.startOf('day').set({ hour, minute: 0, second: 0, millisecond: 0 }) : null;

// Returns the first selectable whole hour on the sensor's first-record date.
const getMinHour = (date, minDate) => {
  if (!date?.hasSame(minDate, 'day')) return 0;
  return minDate.minute || minDate.second || minDate.millisecond ? minDate.hour + 1 : minDate.hour;
};

// Fixes invalid hours when both dates are the same or the end date is today.
const normalizeTimeRange = (range, dateLimits) => {
  const normalized = { ...range };
  const { min: minDate, max: maxDate } = dateLimits;

  // Do not allow whole-hour selections before the sensor's first reading.
  if (normalized.startDate?.hasSame(minDate, 'day')) {
    normalized.startHour = Math.max(normalized.startHour, getMinHour(normalized.startDate, minDate));
  }
  if (normalized.endDate?.hasSame(minDate, 'day')) {
    normalized.endHour = Math.max(normalized.endHour, getMinHour(normalized.endDate, minDate));
  }

  // Do not allow an hour later than the current hour when today is selected.
  if (normalized.startDate?.hasSame(maxDate, 'day')) {
    normalized.startHour = Math.min(normalized.startHour, maxDate.hour);
  }
  if (normalized.endDate?.hasSame(maxDate, 'day')) {
    normalized.endHour = Math.min(normalized.endHour, maxDate.hour);
  }

  // On the same day, the end hour must be later than the start hour.
  if (normalized.endDate && normalized.startDate?.hasSame(normalized.endDate, 'day')) {
    normalized.endHour =
      normalized.endHour <= normalized.startHour ? Math.min(normalized.startHour + 1, maxDate.hour) : normalized.endHour;
  }

  const start = toDateTime(normalized.startDate, normalized.startHour);
  const end = toDateTime(normalized.endDate, normalized.endHour);
  if (start?.isValid && end?.isValid) {
    const latestEnd = start.plus({ days: MAX_CUSTOM_RANGE_DAYS });
    if (end > latestEnd) {
      normalized.endDate = latestEnd.startOf('day');
      normalized.endHour = latestEnd.hour;
    }
  }

  return normalized;
};

// Checks the custom date range and changes it to the format expected by the API.
// If "Current chart range" is selected, it uses the chart's existing time range.
const resolveTimeframe = (form, currentTimeRange, dateLimits) => {
  if (form.rangeMode === 'current') return currentTimeRange;

  // Date pickers and hour fields are stored separately in the form.
  const start = toDateTime(form.startDate, form.startHour);
  const end = toDateTime(form.endDate, form.endHour);

  // Custom downloads must be ordered and stay inside the allowed six months.
  if (
    !start?.isValid ||
    !end?.isValid ||
    start.toMillis() >= end.toMillis() ||
    end.toMillis() - start.toMillis() > MAX_CUSTOM_RANGE_DAYS * 24 * 60 * 60 * 1000 ||
    start.toMillis() < dateLimits.min.toMillis() ||
    end.toMillis() > dateLimits.max.toMillis()
  ) {
    throw new Error('Choose a valid range of up to 31 days, ending now or earlier.');
  }

  return `${start.toFormat("yyyy-MM-dd'T'HH:mm")}_${end.toFormat("yyyy-MM-dd'T'HH:mm")}`;
};

// Hour dropdown used for both the start time and end time.
const HourSelect = ({ label, value, disabled, isOptionDisabled, onChange }) => (
  <TextField
    select
    label={label}
    value={value}
    disabled={disabled}
    onChange={event => onChange(Number(event.target.value))}
    sx={{ width: 116, flexShrink: 0 }}>
    {HOURS.map(option => (
      <MenuItem key={option.value} value={option.value} disabled={isOptionDisabled(option.value)}>
        {option.label}
      </MenuItem>
    ))}
  </TextField>
);

// Shows the date and hour fields for a custom time range.
const TimeRangeSection = ({ form, dateLimits, onChange }) => {
  const maxEndDate = form.startDate?.isValid
    ? DateTime.min(dateLimits.max, toDateTime(form.startDate, form.startHour).plus({ days: MAX_CUSTOM_RANGE_DAYS }))
    : dateLimits.max;

  // Apply a date or hour change, then fix any hours that are no longer valid.
  const updateRange = changes =>
    onChange(
      normalizeTimeRange(
        {
          startDate: form.startDate,
          endDate: form.endDate,
          startHour: form.startHour,
          endHour: form.endHour,
          ...changes,
        },
        dateLimits,
      ),
    );

  const handleStartDateChange = date => {
    const startDate = date?.isValid ? date.startOf('day') : date;

    // Move the end date forward if it is now before the new start date.
    updateRange({
      startDate,
      endDate:
        startDate?.isValid && form.endDate?.isValid && startDate.toMillis() > form.endDate.startOf('day').toMillis()
          ? startDate
          : form.endDate,
    });
  };

  return (
    <Box sx={sectionSx}>
      <FormControl>
        <FormLabel sx={sectionTitleSx}>Data time range</FormLabel>
        <RadioGroup row value={form.rangeMode} onChange={event => onChange({ rangeMode: event.target.value })}>
          <FormControlLabel value="current" control={<Radio />} label="Current chart range" />
          <FormControlLabel value="custom" control={<Radio />} label="Custom dates" />
        </RadioGroup>
      </FormControl>

      {form.rangeMode === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={1}>
            <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <DatePicker
                label="Start date"
                value={form.startDate}
                onChange={handleStartDateChange}
                minDate={dateLimits.min.startOf('day')}
                maxDate={dateLimits.max}
                format="dd/MM/yyyy"
                sx={{ flex: '1 1 170px', minWidth: 0 }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                  popper: { sx: { zIndex: 50010 } },
                }}
              />
              <HourSelect
                label="Start hour"
                value={form.startHour}
                onChange={startHour => updateRange({ startHour })}
                isOptionDisabled={hour =>
                  (form.startDate?.hasSame(dateLimits.min, 'day') && hour < getMinHour(form.startDate, dateLimits.min)) ||
                  (form.startDate?.hasSame(dateLimits.max, 'day') && hour > dateLimits.max.hour) ||
                  (form.endDate && form.startDate?.hasSame(form.endDate, 'day') && hour >= form.endHour)
                }
              />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <DatePicker
                label="End date"
                value={form.endDate}
                onChange={date => updateRange({ endDate: date?.isValid ? date.startOf('day') : date })}
                minDate={form.startDate || dateLimits.min.startOf('day')}
                maxDate={maxEndDate}
                format="dd/MM/yyyy"
                disabled={!form.startDate}
                sx={{ flex: '1 1 170px', minWidth: 0 }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                  popper: { sx: { zIndex: 50010 } },
                }}
              />
              <HourSelect
                label="End hour"
                value={form.endHour}
                disabled={!form.startDate}
                onChange={endHour => updateRange({ endHour })}
                isOptionDisabled={hour =>
                  (form.endDate?.hasSame(dateLimits.min, 'day') && hour < getMinHour(form.endDate, dateLimits.min)) ||
                  (form.endDate?.hasSame(dateLimits.max, 'day') && hour > dateLimits.max.hour) ||
                  (form.endDate?.hasSame(maxEndDate, 'day') && hour > maxEndDate.hour) ||
                  (form.endDate && form.startDate?.hasSame(form.endDate, 'day') && hour <= form.startHour)
                }
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
      )}
    </Box>
  );
};

// -----------------------------------------------------------------------------
// CSV creation and download
// -----------------------------------------------------------------------------

// The API returns a separate list for each data column. This combines readings with
// the same timestamp into one row, then sorts the rows by time.
const measurementsToRows = (measurements, columns) => {
  const rowsByTimestamp = new Map();

  columns.forEach(column => {
    (measurements?.[column] || []).forEach(({ unix_time, value }) => {
      const timestamp = Number(unix_time);
      if (!Number.isFinite(timestamp)) return;

      // Reuse the same row when another column has a reading at this time.
      if (!rowsByTimestamp.has(timestamp)) rowsByTimestamp.set(timestamp, { unix_time: timestamp });
      rowsByTimestamp.get(timestamp)[column] = value;
    });
  });

  return [...rowsByTimestamp.values()].sort((a, b) => a.unix_time - b.unix_time);
};

// Builds the CSV header and data rows. It also handles commas, quotes, and line breaks
// so spreadsheet programs can read the file correctly.
const buildCsv = ({
  measurements,
  columns,
  columnLabels,
  deviceId,
  deviceName,
  idLabel = 'sensor id',
  nameLabel,
  interval,
  calculation,
}) => {
  // First combine the API data into rows, then apply the chosen interval.
  const rows = applyInterval(measurementsToRows(measurements, columns), columns, interval, calculation);
  if (!rows.length) throw new Error('No data was found for this selection.');

  // Add file information and column names before the measurement rows.
  return [
    ['Sol-In Technologies - Indoor Air Quality'],
    [idLabel, deviceId ?? '', ...(nameLabel ? [nameLabel, deviceName ?? ''] : [deviceName ?? ''])],
    ['Time', ...columns.map(column => columnLabels[column] || column)],
    ...rows.map(row => [
      new Date(row.unix_time * 1000).toLocaleString('en-GB'),
      ...columns.map(column => {
        const value = row[column];
        const number = Number(value);
        return value !== '' && value != null && Number.isFinite(number) ? Math.round(number * 10) / 10 : value;
      }),
    ]),
  ]
    .map(row =>
      row
        .map(value => {
          // Quotes keep commas and line breaks inside one CSV cell.
          const text = String(value ?? '');
          return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        })
        .join(','),
    )
    .join('\n');
};

// Opens a browser save dialog when supported, otherwise falls back to a normal download.
const downloadCsv = async (csv, fileName) => {
  const csvFileName = `${fileName.replace(/\.csv$/i, '')}.csv`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  // Supported browsers let the user choose the folder and file name.
  if (window.showSaveFilePicker) {
    try {
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: csvFileName,
        types: [
          {
            description: 'CSV file',
            accept: { 'text/csv': ['.csv'] },
          },
        ],
      });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (saveError) {
      // Closing the save window is not an error and should not close the wizard.
      if (saveError.name === 'AbortError') return false;
      throw saveError;
    }
  }

  // Other browsers download through a temporary hidden link.
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = url;
    link.download = csvFileName;
    document.body.appendChild(link);
    link.click();
  } finally {
    // The link and file URL are only needed while the download starts.
    link.remove();
    URL.revokeObjectURL(url);
  }

  return true;
};

// Creates the default file name from the device name and selected dates.
const getDefaultFileName = (deviceName, currentTimeRange, form) => {
  // Replace characters that may not be safe in a file name.
  const safeName = (deviceName || 'device').replace(/[^a-z0-9-_]+/gi, '-');
  let currentStartDate;
  let currentEndDate;

  // A chart range can be exact dates or a number of milliseconds before now.
  if (typeof currentTimeRange === 'string' && currentTimeRange.includes('_')) {
    [currentStartDate, currentEndDate] = currentTimeRange.split('_');
  } else {
    const end = new Date();
    const start = typeof currentTimeRange === 'number' ? new Date(end.getTime() - currentTimeRange) : end;
    currentStartDate = start.toISOString().slice(0, 10);
    currentEndDate = end.toISOString().slice(0, 10);
  }

  const start = toDateTime(form.startDate, form.startHour)?.toFormat('yyyy-MM-dd-HH-mm') || currentStartDate;
  const end = toDateTime(form.endDate, form.endHour)?.toFormat('yyyy-MM-dd-HH-mm') || currentEndDate;
  return `${safeName}-data-${start}-${end}`;
};

// -----------------------------------------------------------------------------
// Wizard
// -----------------------------------------------------------------------------

// Keeps the user's choices and runs the full download process.
const CsvDownloadWizard = ({
  deviceId,
  deviceName,
  idLabel,
  nameLabel,
  availableColumns = [],
  selectedColumns = [],
  columnLabels = {},
  currentTimeRange,
  firstRecordTime,
  fetchMeasurements,
  onDownloadSuccess,
  onComplete,
}) => {
  const [form, setForm] = useState({
    columns: selectedColumns,
    rangeMode: 'current',
    startDate: null,
    endDate: null,
    startHour: 0,
    endHour: 23,
    interval: 5,
    calculation: 'average',
    fileName: '',
  });
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const availableColumnSet = useMemo(() => new Set(availableColumns), [availableColumns]);
  const dateLimits = useMemo(() => {
    const max = DateTime.local().startOf('hour');
    const firstRecord = DateTime.fromSeconds(Number(firstRecordTime));
    const sixMonthsAgo = max.minus({ months: 6 }).startOf('day');
    return { min: firstRecord.isValid && firstRecord.toSeconds() > 0 ? DateTime.max(sixMonthsAgo, firstRecord) : sixMonthsAgo, max };
  }, [firstRecordTime]);

  // Remove selected columns that are no longer available for this device.
  useEffect(() => {
    setForm(current => ({
      ...current,
      columns: selectedColumns.filter(column => availableColumnSet.has(column)),
    }));
  }, [availableColumnSet, selectedColumns]);

  const updateForm = changes => setForm(current => ({ ...current, ...changes }));
  const defaultFileName = getDefaultFileName(deviceName, currentTimeRange, form);

  const handleDownload = async () => {
    // Check again before downloading in case the available columns changed.
    const columns = form.columns.filter(column => availableColumnSet.has(column));
    if (!columns.length) {
      setError('Select at least one data column.');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      // Load only the selected columns and selected time range.
      const response = await fetchMeasurements(columns, resolveTimeframe(form, currentTimeRange, dateLimits), {
        includeRawRecords: form.interval === 5,
      });

      // Some requests return measurements directly and others wrap them in "data".
      const csv = buildCsv({
        measurements: response?.measurements || response?.data?.measurements,
        columns,
        columnLabels,
        deviceId,
        deviceName,
        idLabel,
        nameLabel,
        interval: form.interval,
        calculation: form.calculation,
      });

      // Keep the wizard open if the user closes the browser's save window.
      const didDownload = await downloadCsv(csv, form.fileName.trim() || defaultFileName);
      if (didDownload) {
        onDownloadSuccess?.();
        onComplete?.();
      }
    } catch (downloadError) {
      setError(downloadError.message || 'Could not create the CSV file.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 3, width: 'min(960px, calc(100vw - 32px))' }}>
      <Typography variant="body2" color="text.secondary">
        Choose exactly what will be included in the export.
      </Typography>
      <CustomSnackbar vertical="top" open={Boolean(error)} onClose={() => setError('')} message={error} severity="error" />

      <DataColumnsSection
        availableColumns={availableColumns}
        columnLabels={columnLabels}
        selectedColumns={form.columns}
        onChange={columns => updateForm({ columns })}
      />
      <TimeRangeSection form={form} dateLimits={dateLimits} onChange={updateForm} />
      <DataIntervalSection interval={form.interval} calculation={form.calculation} onChange={updateForm} />

      <Box sx={sectionSx}>
        <Typography sx={sectionTitleSx}>File name</Typography>
        <TextField
          label="File name"
          value={form.fileName}
          onChange={event => updateForm({ fileName: event.target.value })}
          placeholder={`${defaultFileName}.csv`}
          helperText="Leave empty to use the device name and selected dates."
          fullWidth
        />
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onComplete} disabled={downloading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default CsvDownloadWizard;
